import React, { useEffect, useState } from 'react';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  Button,
  Chip,
  CircularProgress,
  Alert,
  Divider,
  Grid
} from '@mui/material';
import {
  AutorenewRounded as AutorenewIcon,
  StorefrontRounded as StorefrontIcon,
  CreditCardRounded as CreditCardIcon,
  BoltRounded as BoltIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import i18n from '../../i18n';
import CustomerLayout from '../../components/customer/CustomerLayout';
import { AccountSubscription } from '../../types/clientAccount';
import customerApiService from '../../services/customerApi';

// La vieja página de «Mis Suscripciones» enseñaba el producto Paddle de tenants
// (tabla `plans`, suscripciones B2C de tenants). Este portal es de EMPLEADOS IA:
// lo único que un cliente «tiene contratado» de forma recurrente es su PLAN DE
// CRÉDITOS (bolsa mensual que caduca). Esta página reconvierte la vista para
// enseñar ESE plan como una suscripción: sus créditos, cuándo se renueva y su
// ORIGEN. Datos de GET /customer/account/subscription (AccountSubscriptionOut).
//
// Cuenta gestionada por un partner: SÍ ve esta página (es su plan, se lo asigna
// su proveedor), pero NO ve nada de contratar/pagar — coherente con el resto del
// portal (managedByReseller). El origen se toma de `managed_by` del propio
// endpoint, así que no hace falta el contexto para el texto.

const CustomerSubscriptionPageV2: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [subscription, setSubscription] = useState<AccountSubscription | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const data = await customerApiService.getAccountSubscription();
        if (alive) setSubscription(data);
      } catch {
        if (alive) setError(t('customer.subscription.loadError', 'No se pudo cargar tu suscripción'));
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [t]);

  const locale = i18n.language === 'es' ? 'es-ES' : 'en-US';

  // renews_at llega como fecha (YYYY-MM-DD, sin hora): se formatea larga para el
  // cliente. Sin fecha (no debería con plan vigente) cae a «—».
  const formatLongDate = (date?: string | null): string => {
    if (!date) return '—';
    const parsed = new Date(`${date.slice(0, 10)}T00:00:00`);
    return Number.isNaN(parsed.getTime())
      ? '—'
      : parsed.toLocaleDateString(locale, { day: 'numeric', month: 'long', year: 'numeric' });
  };

  const asNumber = (v: number | string | null | undefined): number =>
    v === null || v === undefined ? 0 : Number(v);

  const hasPlan = !!subscription?.has_plan;
  const isManaged = !!subscription?.managed_by;
  const isGateway = subscription?.source === 'gateway';

  // Etiqueta de ORIGEN de la suscripción:
  //  · pasarela (futuro pago propio del cliente directo) → «Pago por pasarela»;
  //  · asignada por un partner → «Asignada por tu proveedor {nombre}»;
  //  · asignada en una cuenta directa (hoy la pone el admin, sin pasarela) →
  //    «Asignada por WAI».
  const originLabel = isGateway
    ? t('customer.subscription.origin.gateway', 'Pago por pasarela')
    : isManaged
      ? t('customer.subscription.origin.assignedByPartner', 'Asignada por tu proveedor {{name}}', {
          name: subscription?.managed_by
        })
      : t('customer.subscription.origin.assignedByWai', 'Asignada por WAI');
  const originIcon = isGateway ? <CreditCardIcon fontSize="small" /> : <StorefrontIcon fontSize="small" />;

  if (loading) {
    return (
      <CustomerLayout>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
          <CircularProgress />
        </Box>
      </CustomerLayout>
    );
  }

  return (
    <CustomerLayout>
      <Container maxWidth="md" sx={{ py: 6 }}>
        {/* Header */}
        <Box mb={4}>
          <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
            {t('customer.subscription.title', 'Suscripción')}
          </Typography>
          <Typography variant="body1" color="text.secondary">
            {t('customer.subscription.planSubtitle', 'Tu plan de créditos: cuánto incluye, cuándo se renueva y de dónde viene')}
          </Typography>
        </Box>

        {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

        {/* Sin plan: estado vacío. Directo → enlace a Créditos; gestionado →
            su proveedor aún no le ha asignado nada (a él se dirige, no a WAI). */}
        {!hasPlan && (
          <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, textAlign: 'center', py: 6, px: 3 }}>
            <BoltIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
            <Typography variant="h6" gutterBottom>
              {t('customer.subscription.noPlanTitle', 'Todavía no tienes un plan de créditos')}
            </Typography>
            {isManaged ? (
              <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 480, mx: 'auto' }}>
                {t('customer.subscription.noPlanManaged',
                  'Tu proveedor {{name}} aún no te ha asignado un plan de créditos. Escríbele si necesitas uno.',
                  { name: subscription?.managed_by })}
              </Typography>
            ) : (
              <>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3, maxWidth: 480, mx: 'auto' }}>
                  {t('customer.subscription.noPlanDirect',
                    'Un plan te da créditos cada mes para que tus empleados IA trabajen. Explora los planes disponibles.')}
                </Typography>
                <Button
                  variant="contained"
                  size="large"
                  onClick={() => navigate('/customer/plans')}
                  sx={{ px: 4, py: 1.5, fontWeight: 600, textTransform: 'none', borderRadius: 2, boxShadow: 'none' }}
                >
                  {t('customer.subscription.viewPlans', 'Ver Planes')}
                </Button>
              </>
            )}
          </Card>
        )}

        {/* Con plan: la «suscripción» = el plan de créditos. */}
        {hasPlan && subscription && (
          <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
            <CardContent sx={{ p: 4 }}>
              <Box display="flex" justifyContent="space-between" alignItems="flex-start" flexWrap="wrap" gap={2} mb={2}>
                <Box>
                  <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
                    {t('customer.subscription.planName', 'Plan de créditos')}
                  </Typography>
                  <Chip
                    label={t('customer.subscription.statusActive', 'Suscripción activa')}
                    color="success"
                    size="small"
                    sx={{ fontWeight: 600 }}
                  />
                </Box>
                <Box textAlign="right">
                  <Typography variant="h4" sx={{ fontWeight: 700, color: 'primary.main', lineHeight: 1.1 }}>
                    {asNumber(subscription.plan_credits).toLocaleString(locale)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t('customer.subscription.creditsPerMonth', 'créditos / mes')}
                  </Typography>
                </Box>
              </Box>

              {/* Origen de la suscripción: chip legible SIEMPRE (para que el
                  cliente gestionado sepa que su plan viene de su proveedor). */}
              <Chip
                icon={originIcon}
                label={originLabel}
                variant="outlined"
                sx={{ mb: 3, fontWeight: 500 }}
              />

              <Divider sx={{ mb: 3 }} />

              <Grid container spacing={3}>
                {/* Renovación: la fecha CONCRETA + el día de ciclo. */}
                <Grid item xs={12} sm={6}>
                  <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                    <AutorenewIcon fontSize="small" color="action" />
                    <Typography variant="body2" color="text.secondary">
                      {t('customer.subscription.renewsOn', 'Se renueva el')}
                    </Typography>
                  </Box>
                  <Typography variant="body1" fontWeight={600}>
                    {formatLongDate(subscription.renews_at)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {t('customer.subscription.cycleDayHint', 'Cada mes el día {{day}}', { day: subscription.cycle_day })}
                  </Typography>
                </Grid>

                {/* Remanente del ciclo vigente (caduca al renovar). */}
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    {t('customer.subscription.remainingThisCycle', 'Disponible este ciclo')}
                  </Typography>
                  <Typography variant="body1" fontWeight={600}>
                    {t('customer.subscription.remainingOfTotal', '{{remaining}} de {{total}} créditos', {
                      remaining: asNumber(subscription.plan_remaining).toLocaleString(locale),
                      total: asNumber(subscription.plan_total).toLocaleString(locale)
                    })}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {t('customer.subscription.expiresHint', 'El remanente no consumido caduca al renovar el ciclo')}
                  </Typography>
                </Grid>
              </Grid>

              {/* Cuenta gestionada: NO se ofrece contratar/pagar — su plan lo
                  gobierna su proveedor. Aviso explícito para que quede claro. */}
              {isManaged && (
                <Alert severity="info" icon={<StorefrontIcon fontSize="small" />} sx={{ mt: 3 }}>
                  {t('customer.subscription.managedNote',
                    'Tu proveedor {{name}} gestiona tu plan de créditos. Contacta con él para cambiarlo o ampliarlo.',
                    { name: subscription.managed_by })}
                </Alert>
              )}

              {/* Cuenta directa: enlace al catálogo de créditos (sin pasarela
                  aún, así que es explorar/pedir, no pagar aquí). */}
              {!isManaged && !isGateway && (
                <Box mt={3}>
                  <Button
                    variant="outlined"
                    onClick={() => navigate('/customer/plans')}
                    sx={{ fontWeight: 600, textTransform: 'none', borderRadius: 2 }}
                  >
                    {t('customer.subscription.viewPlans', 'Ver Planes')}
                  </Button>
                </Box>
              )}
            </CardContent>
          </Card>
        )}
      </Container>
    </CustomerLayout>
  );
};

export default CustomerSubscriptionPageV2;
